import React, { useState, useEffect } from 'react';
import { useToast } from './ToastProvider';
import Icon from './Icon';
import '../styles/FichePalanquier.css';
import { generateFichePDF } from '../utils/generateFichePDF';

const BREVETS = ['', 'P1', 'P2', 'P3', 'N1', 'N2', 'N3', 'PN2', 'PA20', 'PA40', 'PA60', 'E1', 'E2', 'E3', 'E4', 'GP', 'MF1', 'MF2'];
const APTITUDES = ['', 'PA-12', 'PE-20', 'PA-20', 'PE-40', 'PA-40', 'PE-60', 'PA-60', 'Bapt'];
const FONCTIONS = ['', 'M', 'AUT', 'GP', 'ENC'];

function FichePalanquierModal({ session, selectedDate, initialData, onSave, onClose }) {
  const toast = useToast();

  const fmtDate = (d) => {
    if (!d) return new Date().toLocaleDateString('fr-FR');
    return new Date(d + 'T12:00:00').toLocaleDateString('fr-FR');
  };

  const defaultForm = {
    date: fmtDate(selectedDate),
    heure: '09:00',
    site: '',
    bateau: '',
    pilote: '',
    nbPlongeurs: 0,
    dp: session.instructors?.[0]?.name || '',
    dpBrevet: session.instructors?.[0]?.level || '',
    directeurStage: '',
    president: '',
    commentaires: '',
    palanquees: [],
    hostInstructors: [],
  };

  const [formData, setFormData] = useState(() => {
    if (initialData) {
      const { id: _id, savedAt: _sa, ...rest } = initialData;
      return rest;
    }
    return defaultForm;
  });

  const [isSaving, setIsSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(initialData?.savedAt || null);
  const [newHostName, setNewHostName]   = useState('');
  const [newHostLevel, setNewHostLevel] = useState('');

  // Auto-calcule nbPlongeurs = nb de personnes uniques dans toutes les palanquées
  useEffect(() => {
    const ids = new Set();
    formData.palanquees.forEach(pal => pal.plongeurs.forEach(p => ids.add(p.personId)));
    const n = ids.size;
    if (n !== formData.nbPlongeurs) {
      upd('nbPlongeurs', n);
    }
  }, [formData.palanquees]); // eslint-disable-line react-hooks/exhaustive-deps

  const allPersons = [
    ...(session.divers              || []).map(d => ({ ...d, isInstructor: false })),
    ...(session.instructors         || []).map(i => ({ ...i, isInstructor: true  })),
    ...(session.guidePalanquiers    || []).map(g => ({ ...g, isInstructor: true, isGuidePalanquier: true })),
    ...(formData.hostInstructors    || []).map(h => ({ ...h, isInstructor: true, isHostInstructor: true })),
  ];

  const addHostInstructor = () => {
    if (!newHostName.trim()) return;
    const host = { id: 'host-' + Date.now(), name: newHostName.trim(), level: newHostLevel.trim() };
    upd('hostInstructors', [...(formData.hostInstructors || []), host]);
    setNewHostName('');
    setNewHostLevel('');
  };

  const removeHostInstructor = (id) => {
    upd('hostInstructors', (formData.hostInstructors || []).filter(h => h.id !== id));
    // Retirer aussi des palanquées
    setFormData(p => ({
      ...p,
      hostInstructors: (p.hostInstructors || []).filter(h => h.id !== id),
      palanquees: p.palanquees.map(pal => ({
        ...pal,
        plongeurs: pal.plongeurs.filter(d => d.personId !== id),
      })),
    }));
  };

  const upd  = (field, val) => setFormData(p => ({ ...p, [field]: val }));
  const updP = (palId, field, val) => setFormData(p => ({
    ...p,
    palanquees: p.palanquees.map(pl => pl.id === palId ? { ...pl, [field]: val } : pl),
  }));

  const addPalanquee = () => setFormData(p => ({
    ...p,
    palanquees: [...p.palanquees, {
      id: Date.now() + Math.random(),
      numero: p.palanquees.length + 1,
      type: 'exploration', gaz: 'air',
      brevetPreparation: '',
      profPrevu: '', profRealise: '',
      tpsMaxPrevu: '', tpsMaxRealise: '',
      hi: '', hs: '', paliersPrevu: '', paliersRealise: '',
      plongeurs: [],
    }],
  }));

  const removePalanquee = (palId) => setFormData(p => ({
    ...p,
    palanquees: p.palanquees.filter(pl => pl.id !== palId).map((pl, i) => ({ ...pl, numero: i + 1 })),
  }));

  const addPerson = (palId, personId) => {
    const person = allPersons.find(p => p.id === personId);
    if (!person) return;
    setFormData(p => ({
      ...p,
      palanquees: p.palanquees.map(pl => {
        if (pl.id !== palId || pl.plongeurs.some(d => d.personId === personId)) return pl;
        return { ...pl, plongeurs: [...pl.plongeurs, {
          personId: person.id, name: person.name,
          niveau: person.level || '',
          brevet: '', aptitude: '',
          fonction: person.isInstructor ? 'ENC' : 'M',
        }]};
      }),
    }));
  };

  const updPerson = (palId, personId, field, val) => setFormData(p => ({
    ...p,
    palanquees: p.palanquees.map(pl => {
      if (pl.id !== palId) return pl;
      return { ...pl, plongeurs: pl.plongeurs.map(d => d.personId === personId ? { ...d, [field]: val } : d) };
    }),
  }));

  const removePerson = (palId, personId) => setFormData(p => ({
    ...p,
    palanquees: p.palanquees.map(pl => {
      if (pl.id !== palId) return pl;
      return { ...pl, plongeurs: pl.plongeurs.filter(d => d.personId !== personId) };
    }),
  }));

  // ─────────────────────────────────────────────────────────────────────────
  // SAVE
  // ─────────────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await onSave(formData);
      if (result?.savedAt) setSavedAt(result.savedAt);
      toast.success('Fiche sauvegardée !');
    } catch (_) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // PDF
  // ─────────────────────────────────────────────────────────────────────────
  const handleGeneratePDF = async () => {
    try {
      await generateFichePDF(formData, session);
      toast.success('Fiche de sécurité générée !');
    } catch (err) {
      toast.error(err.message || 'Erreur génération PDF');
    }
  };

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  // IDs de toutes les personnes déjà affectées à une palanquée (quelle qu'elle soit)
  const assignedPersonIds = new Set(
    formData.palanquees.flatMap(pl => pl.plongeurs.map(d => d.personId))
  );

  // Tous les participants disponibles comme DP
  const dpCandidates = [
    ...(session.instructors || []),
    ...(session.guidePalanquiers || []),
    ...(session.divers || []),
    ...(formData.hostInstructors || []),
  ];

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content fiche-palanquier-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2><Icon name="clipboard" /> Fiche de Sécurité — Feuille de Palanquée</h2>
          <button onClick={onClose} className="close-btn"><Icon name="close" /></button>
        </div>

        <div className="fiche-form">

          {/* ── Infos générales ── */}
          <div className="fiche-section">
            <h3 className="fiche-section-title"><Icon name="info" /> Informations générales</h3>
            <div className="fiche-grid-4">
              <div className="fiche-field">
                <label>Date</label>
                <input type="text" value={formData.date} onChange={e => upd('date', e.target.value)} />
              </div>
              <div className="fiche-field">
                <label>Heure</label>
                <input type="time" value={formData.heure} onChange={e => upd('heure', e.target.value)} />
              </div>
              <div className="fiche-field">
                <label>Bateau</label>
                <input type="text" value={formData.bateau} onChange={e => upd('bateau', e.target.value)} placeholder="Nom du bateau" />
              </div>
              <div className="fiche-field">
                <label>Pilote</label>
                <input type="text" value={formData.pilote || ''} onChange={e => upd('pilote', e.target.value)} placeholder="Nom du pilote" />
              </div>
              <div className="fiche-field">
                <label>Nb plongeurs</label>
                <input type="number" value={formData.nbPlongeurs} onChange={e => upd('nbPlongeurs', e.target.value)} />
              </div>
            </div>
            <div className="fiche-grid-4">
              <div className="fiche-field">
                <label>Directeur de Plongée</label>
                <select value={formData.dp} onChange={e => {
                  const person = dpCandidates.find(p => p.name === e.target.value);
                  upd('dp', e.target.value);
                  if (person) upd('dpBrevet', person.level || '');
                }}>
                  <option value="">— Sélectionner —</option>
                  {(session.instructors || []).length > 0 && (
                    <optgroup label="Moniteurs">
                      {(session.instructors || []).map(i => (
                        <option key={i.id} value={i.name}>{i.name} ({i.level || '?'})</option>
                      ))}
                    </optgroup>
                  )}
                  {(session.guidePalanquiers || []).length > 0 && (
                    <optgroup label="Guides de Palanquée">
                      {(session.guidePalanquiers || []).map(g => (
                        <option key={g.id} value={g.name}>{g.name} ({g.level || '?'})</option>
                      ))}
                    </optgroup>
                  )}
                  {(formData.hostInstructors || []).length > 0 && (
                    <optgroup label="Moniteurs club hôte">
                      {(formData.hostInstructors || []).map(h => (
                        <option key={h.id} value={h.name}>{h.name} ({h.level || '?'})</option>
                      ))}
                    </optgroup>
                  )}
                  {(session.divers || []).length > 0 && (
                    <optgroup label="Plongeurs">
                      {(session.divers || []).map(d => (
                        <option key={d.id} value={d.name}>{d.name} ({d.level || '?'})</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div className="fiche-field">
                <label>Prof (brevet DP)</label>
                <input type="text" value={formData.dpBrevet} onChange={e => upd('dpBrevet', e.target.value)} placeholder="E4, MF2…" />
              </div>
              <div className="fiche-field">
                <label>Directeur de stage</label>
                <input type="text" value={formData.directeurStage} onChange={e => upd('directeurStage', e.target.value)} placeholder="Nom du directeur de stage" />
              </div>
              <div className="fiche-field">
                <label>Site de plongée</label>
                <input type="text" value={formData.site} onChange={e => upd('site', e.target.value)} placeholder="Nom du site" />
              </div>
            </div>
            <div className="fiche-grid-2">
              <div className="fiche-field">
                <label>Président du stage</label>
                <input type="text" value={formData.president} onChange={e => upd('president', e.target.value)} placeholder="Nom du président" />
              </div>
              <div className="fiche-field">
                <label>Commentaires</label>
                <input type="text" value={formData.commentaires} onChange={e => upd('commentaires', e.target.value)} placeholder="Observations, DP stagiaire…" />
              </div>
            </div>
          </div>

          {/* ── Moniteurs du club hôte ── */}
          <div className="fiche-section">
            <h3 className="fiche-section-title"><Icon name="user" /> Moniteurs du club hôte</h3>
            <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 0.75rem' }}>
              Ajoutez les moniteurs du club qui vous accueille. Ils seront disponibles dans les palanquées et comptabilisés dans le total.
            </p>

            {/* Liste existants */}
            {(formData.hostInstructors || []).length > 0 && (
              <div className="plongeurs-table-section" style={{ marginBottom: '0.75rem' }}>
                <table className="plongeurs-data-table">
                  <thead>
                    <tr><th>Nom</th><th>Brevet / Niveau</th><th></th></tr>
                  </thead>
                  <tbody>
                    {(formData.hostInstructors || []).map(h => (
                      <tr key={h.id}>
                        <td>{h.name}</td>
                        <td>{h.level || '—'}</td>
                        <td><button onClick={() => removeHostInstructor(h.id)} className="btn-remove-chip">✕</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Formulaire ajout */}
            <div className="fiche-grid-3" style={{ alignItems: 'flex-end' }}>
              <div className="fiche-field">
                <label>Nom du moniteur hôte</label>
                <input
                  type="text"
                  value={newHostName}
                  onChange={e => setNewHostName(e.target.value)}
                  placeholder="Prénom Nom"
                  onKeyDown={e => { if (e.key === 'Enter') addHostInstructor(); }}
                />
              </div>
              <div className="fiche-field">
                <label>Brevet / Niveau</label>
                <input
                  type="text"
                  value={newHostLevel}
                  onChange={e => setNewHostLevel(e.target.value)}
                  placeholder="E3, MF1…"
                  onKeyDown={e => { if (e.key === 'Enter') addHostInstructor(); }}
                />
              </div>
              <div className="fiche-field">
                <button
                  onClick={addHostInstructor}
                  className="btn-add-palanquee"
                  style={{ width: '100%' }}
                  disabled={!newHostName.trim()}
                >
                  <Icon name="plus" /> Ajouter
                </button>
              </div>
            </div>
          </div>

          {/* ── Palanquées ── */}
          <div className="fiche-section">
            <div className="fiche-section-header">
              <h3 className="fiche-section-title"><Icon name="users" /> Palanquées ({formData.palanquees.length})</h3>
              <button onClick={addPalanquee} className="btn-add-palanquee">
                <Icon name="plus" /> Ajouter une palanquée
              </button>
            </div>

            {formData.palanquees.length === 0 && (
              <p className="empty-activities" style={{ textAlign: 'center', padding: '1.5rem' }}>
                Aucune palanquée. Cliquez sur "Ajouter une palanquée".
              </p>
            )}

            {formData.palanquees.map(pal => (
              <div key={pal.id} className="palanquee-block">

                {/* Header */}
                <div className="palanquee-block-header">
                  <span className="palanquee-numero">Palanquée N° {pal.numero}</span>
                  <div className="palanquee-controls">
                    <label className="radio-label">
                      <input type="radio" name={`type-${pal.id}`} value="technique"
                        checked={pal.type === 'technique'} onChange={() => updP(pal.id, 'type', 'technique')} />
                      Technique
                    </label>
                    <label className="radio-label">
                      <input type="radio" name={`type-${pal.id}`} value="exploration"
                        checked={pal.type === 'exploration'} onChange={() => updP(pal.id, 'type', 'exploration')} />
                      Exploration
                    </label>
                    <span className="palanquee-sep">|</span>
                    <label className="radio-label">
                      <input type="radio" name={`gaz-${pal.id}`} value="air"
                        checked={pal.gaz === 'air'} onChange={() => updP(pal.id, 'gaz', 'air')} />
                      Air
                    </label>
                    <label className="radio-label">
                      <input type="radio" name={`gaz-${pal.id}`} value="nitrox"
                        checked={pal.gaz === 'nitrox'} onChange={() => updP(pal.id, 'gaz', 'nitrox')} />
                      Nitrox
                    </label>
                    <button onClick={() => removePalanquee(pal.id)} className="btn-remove-palanquee">
                      <Icon name="trash" />
                    </button>
                  </div>
                </div>

                {/* Paramètres */}
                <div className="palanquee-params">
                  <div className="fiche-field-sm" style={{ gridColumn: 'span 2' }}>
                    <label>Brevet en préparation</label>
                    <input type="text" value={pal.brevetPreparation}
                      onChange={e => updP(pal.id, 'brevetPreparation', e.target.value)} placeholder="N2, N3…" />
                  </div>
                  <div className="fiche-field-sm">
                    <label>Prof. prévue (m)</label>
                    <input type="number" value={pal.profPrevu}
                      onChange={e => updP(pal.id, 'profPrevu', e.target.value)} placeholder="40" />
                  </div>
                  <div className="fiche-field-sm">
                    <label>Prof. réalisée (m)</label>
                    <input type="number" value={pal.profRealise}
                      onChange={e => updP(pal.id, 'profRealise', e.target.value)} />
                  </div>
                  <div className="fiche-field-sm">
                    <label>Tps Max prévu (mn)</label>
                    <input type="number" value={pal.tpsMaxPrevu}
                      onChange={e => updP(pal.id, 'tpsMaxPrevu', e.target.value)} placeholder="45" />
                  </div>
                  <div className="fiche-field-sm">
                    <label>Tps Max réalisé (mn)</label>
                    <input type="number" value={pal.tpsMaxRealise}
                      onChange={e => updP(pal.id, 'tpsMaxRealise', e.target.value)} />
                  </div>
                  <div className="fiche-field-sm">
                    <label>HI — Heure d'immersion</label>
                    <input type="time" value={pal.hi} onChange={e => updP(pal.id, 'hi', e.target.value)} />
                  </div>
                  <div className="fiche-field-sm">
                    <label>HS — Heure de sortie</label>
                    <input type="time" value={pal.hs} onChange={e => updP(pal.id, 'hs', e.target.value)} />
                  </div>
                  <div className="fiche-field-sm">
                    <label>Paliers prévus</label>
                    <input type="text" value={pal.paliersPrevu ?? pal.paliers ?? ''}
                      onChange={e => updP(pal.id, 'paliersPrevu', e.target.value)} placeholder="3 min à 3 m…" />
                  </div>
                  <div className="fiche-field-sm">
                    <label>Paliers réalisés</label>
                    <input type="text" value={pal.paliersRealise ?? ''}
                      onChange={e => updP(pal.id, 'paliersRealise', e.target.value)} placeholder="3 min à 3 m…" />
                  </div>
                </div>

                {/* Plongeurs */}
                <div className="plongeurs-table-section">
                  <div className="plongeurs-table-header">
                    <span>Plongeurs de la palanquée ({pal.plongeurs.length})</span>
                    <select className="add-person-select" value=""
                      onChange={e => { if (e.target.value) addPerson(pal.id, e.target.value); }}>
                      <option value="">+ Ajouter un plongeur / moniteur</option>
                      {allPersons
                        .filter(p => !assignedPersonIds.has(p.id))
                        .map(p => (
                          <option key={p.id} value={p.id}>
                            {p.name} ({p.level || '?'}) {p.isGuidePalanquier ? '— Guide GP' : p.isInstructor ? '— Moniteur' : '— Plongeur'}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  {pal.plongeurs.length === 0 && (
                    <p className="empty-activities" style={{ padding: '0.75rem 1rem', margin: 0 }}>
                      Aucun plongeur. Utilisez le sélecteur ci-dessus.
                    </p>
                  )}

                  {pal.plongeurs.length > 0 && (
                    <table className="plongeurs-data-table">
                      <thead>
                        <tr>
                          <th>Prénom Nom</th>
                          <th>Niveau</th>
                          <th>Brevet</th>
                          <th>Aptitude</th>
                          <th>Fonction</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {pal.plongeurs.map(d => (
                          <tr key={d.personId}>
                            <td>{d.name}</td>
                            <td style={{ fontSize: '0.78rem', color: '#64748b' }}>{d.niveau || '—'}</td>
                            <td>
                              <select value={d.brevet || ''}
                                onChange={e => updPerson(pal.id, d.personId, 'brevet', e.target.value)}
                                style={{ width: '100%' }}>
                                {BREVETS.map(b => <option key={b || 'empty-brevet'} value={b}>{b || '—'}</option>)}
                              </select>
                            </td>
                            <td>
                              <select value={d.aptitude || ''}
                                onChange={e => updPerson(pal.id, d.personId, 'aptitude', e.target.value)}>
                                {APTITUDES.map(a => <option key={a || 'empty-aptitude'} value={a}>{a || '—'}</option>)}
                              </select>
                            </td>
                            <td>
                              <select value={d.fonction || ''}
                                onChange={e => updPerson(pal.id, d.personId, 'fonction', e.target.value)}>
                                {FONCTIONS.map(f => <option key={f || 'empty-fonction'} value={f}>{f || '—'}</option>)}
                              </select>
                            </td>
                            <td>
                              <button onClick={() => removePerson(pal.id, d.personId)} className="btn-remove-chip">✕</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>

              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="fiche-modal-footer">
          <div className="fiche-footer-left">
            {savedAt && (
              <span className="fiche-saved-badge">
                <Icon name="check" /> Sauvegardée le {new Date(savedAt).toLocaleString('fr-FR')}
              </span>
            )}
          </div>
          <div className="fiche-footer-right">
            <button onClick={onClose} className="btn-cancel-fiche">Fermer</button>
            <button onClick={handleSave} className="btn-save-fiche" disabled={isSaving}>
              <Icon name="save" /> {isSaving ? 'Sauvegarde...' : 'Sauvegarder'}
            </button>
            <button onClick={handleGeneratePDF} className="btn-generate-pdf">
              <Icon name="pdf" /> Générer la fiche PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default FichePalanquierModal;
